import React, { useState, useEffect } from 'react';
import { getTimeRemaining, formatCountdown, TimeRemaining } from '../../utils/dateUtils';

interface CountdownTimerProps {
    targetDate: string | Date;
    onEnd?: () => void;
    className?: string;
    label?: string;
    showLabels?: boolean;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({
    targetDate,
    onEnd,
    className = '',
    label,
    showLabels = true
}) => {
    const [timeLeft, setTimeLeft] = useState<TimeRemaining>(getTimeRemaining(targetDate));

    useEffect(() => {
        if (timeLeft.totalSeconds <= 0) return;

        const timer = setInterval(() => {
            const remaining = getTimeRemaining(targetDate);
            setTimeLeft(remaining);

            if (remaining.totalSeconds <= 0) {
                clearInterval(timer);
                onEnd?.();
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [targetDate, onEnd, timeLeft.totalSeconds]);

    if (timeLeft.totalSeconds <= 0) {
        return null;
    }

    const TimeUnit: React.FC<{ value: number; label: string }> = ({ value, label }) => (
        <div className="flex flex-col items-center mx-1">
            <div className="text-lg sm:text-2xl font-bold font-mono min-w-[1.5em] text-center">
                {value.toString().padStart(2, '0')}
            </div>
            {showLabels && (
                <div className="text-[10px] uppercase tracking-tighter opacity-70">
                    {label}
                </div>
            )}
        </div>
    );

    return (
        <div className={`flex flex-col items-center ${className}`}>
            {label && <div className="text-xs font-semibold mb-1 uppercase tracking-widest opacity-80">{label}</div>}
            <div className="flex items-center">
                <TimeUnit value={timeLeft.days} label="Days" />
                <span className="text-xl font-bold mb-3">:</span>
                <TimeUnit value={timeLeft.hours} label="Hrs" />
                <span className="text-xl font-bold mb-3">:</span>
                <TimeUnit value={timeLeft.minutes} label="Min" />
                <span className="text-xl font-bold mb-3">:</span>
                <TimeUnit value={timeLeft.seconds} label="Sec" />
            </div>
        </div>
    );
};

export default CountdownTimer;
